// Amatangazo.jsx — Announcements with text, image preview & PDF viewer
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useConfirmDelete, useToast } from './ConfirmDialog';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Build absolute URL for stored paths like /uploads/amatangazo/file.pdf
const toUrl = (p) => {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  const clean = p.startsWith('/') ? p : '/' + p;
  return `${API_BASE}${clean}`;
};

const isImage = (p = '') => /\.(jpe?g|png|webp|gif)$/i.test(p);
const isPdf   = (p = '') => /\.pdf$/i.test(p);

// A stored attachment is valid only if it looks like a real path
const isValidAttachment = (v) =>
  v && typeof v === 'string' && v.trim() !== '' && v !== 'attachment' && v.includes('/');

const priorityColor = (p) =>
  ({ Urgent: '#dc2626', High: '#d97706', Normal: '#2563eb', Low: '#16a34a' }[p] || '#2563eb');

// ─── Modal ────────────────────────────────────────────────────
const Modal = ({ onClose, children }) => (
  <div onClick={onClose} style={S.overlay}>
    <div onClick={(e) => e.stopPropagation()} style={S.modalBox}>
      <button onClick={onClose} style={S.modalClose}>✕</button>
      {children}
    </div>
  </div>
);

// ─── AttachmentView ───────────────────────────────────────────
const AttachmentView = ({ attachment }) => {
  const [modal, setModal] = useState(null);

  if (!isValidAttachment(attachment)) return null;

  const url  = toUrl(attachment);
  const name = attachment.split('/').pop();

  return (
    <>
      {isImage(attachment) && (
        <div style={{ marginTop: 12 }}>
          <img
            src={url}
            alt="itangazo"
            onClick={() => setModal('image')}
            style={S.thumbImg}
            title="Kanda urebe ifoto yose"
          />
          <div style={{ marginTop: 6 }}>
            <button onClick={() => setModal('image')} style={S.attachBtn}>🖼️ Reba Ifoto</button>
          </div>
        </div>
      )}

      {isPdf(attachment) && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={S.fileChip}>📄 {name}</span>
          <button onClick={() => setModal('pdf')} style={S.attachBtn}>📄 Fungura Document</button>
        </div>
      )}

      {!isImage(attachment) && !isPdf(attachment) && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={S.fileChip}>📎 {name}</span>
          <a href={url} target="_blank" rel="noopener noreferrer" style={S.attachBtn}>↗ Fungura Dosiye</a>
        </div>
      )}

      {modal === 'image' && (
        <Modal onClose={() => setModal(null)}>
          <img
            src={url}
            alt="full"
            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, display: 'block' }}
          />
        </Modal>
      )}

      {modal === 'pdf' && (
        <Modal onClose={() => setModal(null)}>
          <div style={{ width: '75vw', height: '80vh' }}>
            <iframe
              src={url}
              title="PDF Viewer"
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
            />
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <a href={url} target="_blank" rel="noopener noreferrer" style={S.attachBtn}>
                ↗ Fungura muri tab nshya
              </a>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

// ─── FilePreview (inside form) ────────────────────────────────
const FilePreview = ({ file, localPreview, savedUrl, onClear }) => {
  if (!file && !isValidAttachment(savedUrl)) return null;

  if (file) {
    return (
      <div style={S.previewBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, color: '#0369a1', fontSize: 13 }}>
            {isPdf(file.name) ? '📄' : '🖼️'} {file.name}{' '}
            <span style={{ color: '#888', fontWeight: 400 }}>({(file.size / 1024).toFixed(1)} KB)</span>
          </span>
          <button type="button" onClick={onClear} style={S.clearBtn}>✕</button>
        </div>
        {localPreview && isImage(file.name) && (
          <img
            src={localPreview}
            alt="preview"
            style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, marginTop: 8, objectFit: 'contain', display: 'block' }}
          />
        )}
      </div>
    );
  }

  const url = toUrl(savedUrl);
  return (
    <div style={S.previewBox}>
      {isImage(savedUrl) && (
        <img
          src={url}
          alt="saved"
          style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'contain', display: 'block', marginBottom: 8 }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#0369a1' }}>
          {isPdf(savedUrl) ? '📄' : '🖼️'} {savedUrl.split('/').pop()}
        </span>
        <button type="button" onClick={onClear} style={{ ...S.clearBtn, fontSize: 13 }}>✕ Gusiba</button>
      </div>
    </div>
  );
};

// ─── AnnouncementCard ─────────────────────────────────────────
const AnnouncementCard = ({ item, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const msg = (item.message || '').trim();
  const truncated = msg.length > 200 && !expanded;
  const displayMsg = truncated ? msg.substring(0, 200) : msg;

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {item.title && <div style={S.cardTitle}>{item.title}</div>}
          <div style={S.cardMeta}>
            <span style={{ ...S.badge, background: priorityColor(item.priority) }}>
              {item.priority || 'Normal'}
            </span>
            {item.expiry_date && (
              <span style={S.expiryBadge}>
                ⏰ {new Date(item.expiry_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => onEdit(item)} style={S.editBtn} title="Vugurura">✏️</button>
          <button onClick={() => onDelete(item.id)} style={S.deleteBtn} title="Siba">🗑️</button>
        </div>
      </div>

      {msg ? (
        <div style={S.cardBody}>
          <span style={{ whiteSpace: 'pre-wrap' }}>{displayMsg}</span>
          {msg.length > 200 && (
            <button onClick={() => setExpanded(p => !p)} style={S.expandBtn}>
              {expanded ? ' Gabanya' : '... Soma byose'}
            </button>
          )}
        </div>
      ) : null}

      <AttachmentView attachment={item.attachment} />

      <div style={S.cardFooter}>
        🕐 {new Date(item.created_at).toLocaleString()}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
const ALLOWED_MIME = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const ALLOWED_EXT  = /\.(pdf|jpe?g|png|webp|gif)$/i;
const MAX_MB = 5;

const Amatangazo = () => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [view, setView]             = useState('list');
  const [editing, setEditing]       = useState(null);

  const [form, setForm] = useState({ title: '', message: '', priority: 'Normal', expiry_date: '' });
  const [file, setFile]             = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [savedUrl, setSavedUrl]     = useState('');
  const [fileError, setFileError]   = useState('');
  const fileRef = useRef(null);
  const confirmDelete = useConfirmDelete();
  const { toast } = useToast();

  const fetchItems = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axiosInstance.get('/amatangazo');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Ntibyashobotse gukuramo amatangazo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const clearFile = () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
    setFile(null); setLocalPreview(null); setSavedUrl(''); setFileError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileSelect = (f) => {
    setFileError('');
    if (!f) { clearFile(); return; }
    if (!ALLOWED_MIME.has(f.type) || !ALLOWED_EXT.test(f.name)) {
      setFileError('Dosiye igomba kuba PDF, JPG, PNG cyangwa WEBP'); return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setFileError(`Dosiye ntigomba kurenza ${MAX_MB}MB`); return;
    }
    setFile(f);
    setSavedUrl('');
    setLocalPreview(isImage(f.name) ? URL.createObjectURL(f) : null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', message: '', priority: 'Normal', expiry_date: '' });
    clearFile(); setError(''); setSuccess(''); setView('form');
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title:       item.title || '',
      message:     item.message || '',
      priority:    item.priority || 'Normal',
      expiry_date: item.expiry_date ? item.expiry_date.substring(0, 10) : '',
    });
    clearFile();
    if (isValidAttachment(item.attachment)) setSavedUrl(item.attachment);
    setError(''); setSuccess(''); setView('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { setError('Ubutumwa ni ngombwa'); return; }
    if (fileError) { setError(fileError); return; }
    setSubmitting(true); setError('');
    try {
      const fd = new FormData();
      fd.append('title',    form.title);
      fd.append('message',  form.message);
      fd.append('priority', form.priority);
      if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
      if (file) fd.append('file', file);

      if (editing) {
        await axiosInstance.put(`/amatangazo/${editing.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSuccess('✅ Itangazo ryavuguruwe neza!');
      } else {
        await axiosInstance.post('/amatangazo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSuccess('✅ Itangazo ryanditswe neza!');
      }
      setView('list');
      await fetchItems();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Habaye ikosa');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirmDelete({
      title: 'Gusiba iri tangazo?',
      message: 'Urashaka koko gusiba iri tangazo? Iki gikorwa ntigishobora gusubizwa inyuma.',
      confirmText: '🗑️ Siba Itangazo',
    });
    if (!ok) return;
    try {
      await axiosInstance.delete(`/amatangazo/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast({ type: 'success', title: 'Itangazo ryasibwe', message: 'Itangazo ryasibwe neza.' });
    } catch {
      toast({ type: 'danger', title: 'Ikosa', message: 'Ntibyashobotse gusiba itangazo.' });
    }
  };

  return (
    <div style={S.root}>
      {error   && <div style={S.alertErr}>{error}   <button onClick={() => setError('')}   style={S.alertX}>×</button></div>}
      {success && <div style={S.alertOk}>{success} <button onClick={() => setSuccess('')} style={S.alertX}>×</button></div>}

      <div style={S.header}>
        <div>
          <h2 style={S.pageTitle}>📢 Amatangazo</h2>
          <div style={S.pageSub}>{items.length} itangazo</div>
        </div>
        {view === 'list'
          ? <button onClick={openCreate} style={S.primaryBtn}>✏️ Itangazo Rishya</button>
          : <button onClick={() => setView('list')} style={S.backBtn}>← Subira inyuma</button>}
      </div>

      {/* ── FORM ── */}
      {view === 'form' && (
        <div style={S.formCard}>
          <h3 style={S.formTitle}>{editing ? '✏️ Vugurura Itangazo' : '📢 Itangazo Rishya'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={S.fg}>
              <label style={S.lbl}>📝 Umutwe (optional)</label>
              <input
                style={S.inp}
                type="text"
                placeholder="Umutwe w'itangazo..."
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              />
            </div>

            <div style={S.fg}>
              <label style={S.lbl}>💬 Ubutumwa *</label>
              <textarea
                style={S.ta}
                placeholder="Andika ubutumwa hano..."
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                required
              />
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ ...S.fg, flex: 1 }}>
                <label style={S.lbl}>⚡ Priority</label>
                <select
                  style={S.sel}
                  value={form.priority}
                  onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                >
                  <option value="Low">🟢 Low</option>
                  <option value="Normal">🔵 Normal</option>
                  <option value="High">🟡 High</option>
                  <option value="Urgent">🔴 Urgent</option>
                </select>
              </div>
              <div style={{ ...S.fg, flex: 1 }}>
                <label style={S.lbl}>⏰ Izarangirira (optional)</label>
                <input
                  style={S.inp}
                  type="date"
                  value={form.expiry_date}
                  onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))}
                />
              </div>
            </div>

            <div style={S.fg}>
              <label style={S.lbl}>📎 Inyandiko / Ifoto (optional)</label>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/*"
                style={{ display: 'none' }}
                id="ita-file"
                onChange={e => handleFileSelect(e.target.files?.[0] || null)}
              />
              <label htmlFor="ita-file" style={S.fileLabel}>📁 Hitamo dosiye...</label>
              {fileError && <div style={S.fileErr}>❌ {fileError}</div>}
              <FilePreview
                file={file}
                localPreview={localPreview}
                savedUrl={savedUrl}
                onClear={clearFile}
              />
            </div>

            <button
              type="submit"
              style={{ ...S.primaryBtn, width: '100%', marginTop: 8 }}
              disabled={submitting}
            >
              {submitting ? '⏳ Kohereza...' : editing ? '✏️ Vugurura' : '📢 Ohereza Itangazo'}
            </button>
          </form>
        </div>
      )}

      {/* ── LIST ── */}
      {view === 'list' && (
        loading ? (
          <div style={S.empty}>⏳ Gutegereza...</div>
        ) : items.length === 0 ? (
          <div style={S.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontWeight: 600, color: '#333' }}>Nta matangazo ahari</div>
            <div style={{ color: '#888', fontSize: 14, marginTop: 4 }}>Kanda "Itangazo Rishya" kugira utangire</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {items.map(item => (
              <AnnouncementCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────
const S = {
  root:       { fontFamily: 'system-ui,-apple-system,sans-serif', maxWidth: 860, margin: '0 auto', padding: '0 4px' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  pageTitle:  { fontSize: 22, fontWeight: 700, color: '#1a2332', margin: 0 },
  pageSub:    { fontSize: 13, color: '#888', marginTop: 2 },
  primaryBtn: { padding: '10px 22px', background: 'linear-gradient(135deg,#003580,#1a56b0)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', minHeight: 44 },
  backBtn:    { padding: '10px 18px', background: '#fff', border: '1px solid #dde3ea', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#555', minHeight: 44 },
  alertErr:   { padding: '12px 16px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 },
  alertOk:    { padding: '12px 16px', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 },
  alertX:     { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'inherit', padding: '0 4px' },
  formCard:   { background: '#fff', borderRadius: 14, padding: '24px 28px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: 24 },
  formTitle:  { fontSize: 18, fontWeight: 700, color: '#1a2332', marginBottom: 20 },
  fg:         { marginBottom: 18 },
  lbl:        { display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 },
  inp:        { width: '100%', padding: '12px 14px', border: '2px solid #e8e8e8', borderRadius: 10, fontSize: 15, outline: 'none', boxSizing: 'border-box', background: '#fafafa', color: '#1a2332' },
  ta:         { width: '100%', padding: '12px 14px', border: '2px solid #e8e8e8', borderRadius: 10, fontSize: 15, outline: 'none', minHeight: 130, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fafafa', color: '#1a2332' },
  sel:        { width: '100%', padding: '12px 14px', border: '2px solid #e8e8e8', borderRadius: 10, fontSize: 15, outline: 'none', background: '#fafafa', color: '#1a2332', cursor: 'pointer', boxSizing: 'border-box' },
  fileLabel:  { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: '#f8f9fa', border: '2px dashed #ddd', borderRadius: 10, cursor: 'pointer', fontSize: 14, color: '#555', minHeight: 44 },
  fileErr:    { marginTop: 8, padding: '8px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: 8, fontSize: 13 },
  previewBox: { marginTop: 12, padding: 14, background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd' },
  clearBtn:   { background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, padding: '2px 6px', fontWeight: 700 },
  // card
  card:       { background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderLeft: '4px solid #003580' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 },
  cardTitle:  { fontSize: 16, fontWeight: 700, color: '#1a2332', marginBottom: 6 },
  cardMeta:   { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  badge:      { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#fff' },
  expiryBadge:{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' },
  cardBody:   { fontSize: 14, color: '#444', lineHeight: 1.75, marginBottom: 10, background: '#f9fafb', borderRadius: 8, padding: '10px 14px' },
  expandBtn:  { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 },
  cardFooter: { fontSize: 12, color: '#aaa', borderTop: '1px solid #f0f2f5', paddingTop: 10, marginTop: 12 },
  editBtn:    { padding: '6px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, cursor: 'pointer', fontSize: 14, minHeight: 36 },
  deleteBtn:  { padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, cursor: 'pointer', fontSize: 14, minHeight: 36 },
  // attachment
  thumbImg:   { maxWidth: '100%', maxHeight: 220, borderRadius: 8, objectFit: 'contain', display: 'block', cursor: 'pointer', border: '1px solid #e5e7eb' },
  attachBtn:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, color: '#0369a1', textDecoration: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  fileChip:   { fontSize: 13, color: '#555', background: '#f3f4f6', padding: '4px 10px', borderRadius: 6, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // modal
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  modalBox:   { position: 'relative', background: '#fff', borderRadius: 12, padding: 16, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' },
  modalClose: { position: 'absolute', top: 8, right: 10, background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 16, fontWeight: 700, color: '#333', zIndex: 1 },
  empty:      { textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', color: '#888' },
};

export default Amatangazo;
